import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import PersonIcon from "@mui/icons-material/Person";
import GavelIcon from "@mui/icons-material/Gavel";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import phone from "phone";
import { setUserInfo } from "../redux/slices/userSlice";
import useApi from "../hooks/useApi";
import { SELECT_USER_TYPE } from "../utils/apiConstants";
import { toast } from "../utils/ToastNotification";

export default function UserTypeSelectionModal() {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);
  const mode = useSelector((state) => state.theme?.mode);
  const { apiRequest } = useApi();

  const [selectedType, setSelectedType] = useState("client");
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      role: "",
      mobileNumber: "",
      startedYear: "",
    },
  });

  const userId = userInfo?.userId || userInfo?.id;
  const isOpen = Boolean(
    userId && (!userInfo?.userType || userInfo?.userType.trim() === "")
  );

  if (!isOpen) return null;

  const isDark = mode !== "light";

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const payload = {
        userId,
        userType: selectedType,
      };

      if (selectedType === "advocate") {
        payload.role = values.role?.trim();
        payload.mobileNumber = values.mobileNumber;
        payload.startedYear = values.startedYear;
      }

      const response = await apiRequest({
        url: SELECT_USER_TYPE,
        method: "post",
        data: payload,
      });

      setLoading(false);

      if (response?.status === true) {
        toast.success(response.message || "Profile updated successfully!");
        dispatch(
          setUserInfo({
            ...userInfo,
            userType: response.data?.userType || selectedType,
            role: response.data?.role || values.role,
            mobileNumber: response.data?.mobileNumber || values.mobileNumber,
            startedYear: response.data?.startedYear || values.startedYear,
          })
        );
      } else {
        toast.error(response?.message || "Failed to update profile");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error in select user type:", error);
      toast.error(
        error?.response?.data?.message || "Failed to update user profile"
      );
    }
  };

  const handleCardClick = (type) => {
    setSelectedType(type);
    clearErrors();
  };

  return (
    <Dialog
      open={true}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
      }}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(6px)",
          },
          onClick: (e) => e.stopPropagation(),
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          padding: "20px",
          backgroundColor: isDark ? "#1a1b26 !important" : "#ffffff !important",
          color: isDark ? "#ffffff !important" : "#111111 !important",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.6) !important",
          border: isDark ? "1px solid #2d2f45 !important" : "1px solid #e5e7eb !important",
          zIndex: 1400,
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.6rem",
            color: isDark ? "#ffffff !important" : "#111111 !important",
            pt: 1,
            pb: 1,
          }}
        >
          Complete Your Profile
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 3,
              color: isDark ? "#a0a0c0 !important" : "#555555 !important",
              fontSize: "0.95rem",
            }}
          >
            Please select your account type to continue using Law Blocks AI.
          </Typography>

          <Box display="flex" gap={2} mb={3}>
            <Card
              onClick={() => handleCardClick("client")}
              sx={{
                flex: 1,
                cursor: "pointer",
                borderRadius: "14px",
                border:
                  selectedType === "client"
                    ? "2px solid #7c4dff !important"
                    : isDark
                    ? "1px solid #333348 !important"
                    : "1px solid #e2e8f0 !important",
                backgroundColor:
                  selectedType === "client"
                    ? isDark
                      ? "rgba(124, 77, 255, 0.22) !important"
                      : "#f3e8ff !important"
                    : isDark
                    ? "#242535 !important"
                    : "#f8fafc !important",
                transition: "all 0.2s ease",
              }}
            >
              <CardContent sx={{ textAlign: "center", p: 2.5 }}>
                <PersonIcon
                  sx={{
                    fontSize: 44,
                    color: selectedType === "client" ? "#7c4dff" : isDark ? "#8888aa" : "#64748b",
                    mb: 1,
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    fontSize: "1.1rem",
                    color:
                      selectedType === "client"
                        ? isDark
                          ? "#b388ff !important"
                          : "#5e35b1 !important"
                        : isDark
                        ? "#ffffff !important"
                        : "#1e293b !important",
                  }}
                >
                  Normal User
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#a0a0c0 !important" : "#64748b !important", display: "block" }}
                >
                  Seeking legal support or services
                </Typography>
              </CardContent>
            </Card>

            <Card
              onClick={() => handleCardClick("advocate")}
              sx={{
                flex: 1,
                cursor: "pointer",
                borderRadius: "14px",
                border:
                  selectedType === "advocate"
                    ? "2px solid #7c4dff !important"
                    : isDark
                    ? "1px solid #333348 !important"
                    : "1px solid #e2e8f0 !important",
                backgroundColor:
                  selectedType === "advocate"
                    ? isDark
                      ? "rgba(124, 77, 255, 0.22) !important"
                      : "#f3e8ff !important"
                    : isDark
                    ? "#242535 !important"
                    : "#f8fafc !important",
                transition: "all 0.2s ease",
              }}
            >
              <CardContent sx={{ textAlign: "center", p: 2.5 }}>
                <GavelIcon
                  sx={{
                    fontSize: 44,
                    color: selectedType === "advocate" ? "#7c4dff" : isDark ? "#8888aa" : "#64748b",
                    mb: 1,
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    fontSize: "1.1rem",
                    color:
                      selectedType === "advocate"
                        ? isDark
                          ? "#b388ff !important"
                          : "#5e35b1 !important"
                        : isDark
                        ? "#ffffff !important"
                        : "#1e293b !important",
                  }}
                >
                  Advocate / Attorney
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#a0a0c0 !important" : "#64748b !important", display: "block" }}
                >
                  Offering legal expertise & services
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {selectedType === "advocate" && (
            <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  mb={0.5}
                  sx={{ color: isDark ? "#e0e0ff !important" : "#333333 !important" }}
                >
                  Role / Specialization *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="EX: Criminal Lawyer"
                  size="small"
                  error={!!errors.role}
                  {...register("role", {
                    required: selectedType === "advocate" ? "Role is required (e.g. Criminal Lawyer)" : false,
                  })}
                  sx={{
                    backgroundColor: "transparent !important",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "transparent !important",
                      "& fieldset": {
                        borderColor: errors.role
                          ? "#d32f2f !important"
                          : isDark
                          ? "rgba(255, 255, 255, 0.3) !important"
                          : "rgba(0, 0, 0, 0.3) !important",
                      },
                      "&:hover fieldset": {
                        borderColor: errors.role ? "#d32f2f !important" : "#7c4dff !important",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: errors.role ? "#d32f2f !important" : "#7c4dff !important",
                      },
                    },
                    input: {
                      color: isDark ? "#ffffff !important" : "#000000 !important",
                      backgroundColor: "transparent !important",
                    },
                  }}
                />
                {errors.role && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block", textAlign: "left" }}
                  >
                    {errors.role.message}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  mb={0.5}
                  sx={{ color: isDark ? "#e0e0ff !important" : "#333333 !important" }}
                >
                  Mobile Number *
                </Typography>
                <Controller
                  name="mobileNumber"
                  control={control}
                  rules={{
                    required: selectedType === "advocate" ? "Phone number is required" : false,
                    validate: (value) => {
                      if (selectedType !== "advocate") return true;
                      if (!value?.trim()) return "Phone number is required";
                      const phoneValidation = phone("+" + value.replace(/^\+/, ""));
                      return phoneValidation.isValid || "Please enter a valid phone number";
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <ReactPhoneInput
                      country="us"
                      placeholder="Enter your phone number"
                      containerClass="d-flex flex-row-reverse"
                      inputClass={`h-100 position-static p-0 ps-3 ${
                        isDark ? "bg-transparent txt-white text-white" : "bg-transparent txt-black"
                      }`}
                      buttonClass="position-static p-0 px-2 bg-transparent border-0 border-end"
                      dropdownClass={isDark ? "bg-dark text-white" : "bg-white txt-black"}
                      inputStyle={{
                        width: "100%",
                        height: "40px",
                        color: isDark ? "#ffffff" : "#000000",
                        backgroundColor: "transparent",
                        borderColor: errors.mobileNumber
                          ? "#d32f2f"
                          : isDark
                          ? "rgba(255, 255, 255, 0.3)"
                          : "rgba(0, 0, 0, 0.3)",
                      }}
                      containerStyle={{ height: "40px" }}
                      value={value}
                      onChange={(val, data) => {
                        const countryCode = `+${data.dialCode}`;
                        const nationalNumber = val.slice(data.dialCode.length);
                        const formattedValue = `${countryCode} ${nationalNumber}`;
                        onChange(formattedValue);
                      }}
                    />
                  )}
                />
                {errors.mobileNumber && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block", textAlign: "left" }}
                  >
                    {errors.mobileNumber.message}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  mb={0.5}
                  sx={{ color: isDark ? "#e0e0ff !important" : "#333333 !important" }}
                >
                  Started Year *
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="Enter Started Year (e.g. 2018)"
                  size="small"
                  error={!!errors.startedYear}
                  {...register("startedYear", {
                    required: selectedType === "advocate" ? "Started year is required" : false,
                    validate: (val) => {
                      if (selectedType !== "advocate") return true;
                      const currentYear = new Date().getFullYear();
                      const yearNum = parseInt(val, 10);
                      if (isNaN(yearNum) || yearNum > currentYear || yearNum < 1950) {
                        return `Started year must be between 1950 and ${currentYear}`;
                      }
                      return true;
                    },
                  })}
                  sx={{
                    backgroundColor: "transparent !important",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "transparent !important",
                      "& fieldset": {
                        borderColor: errors.startedYear
                          ? "#d32f2f !important"
                          : isDark
                          ? "rgba(255, 255, 255, 0.3) !important"
                          : "rgba(0, 0, 0, 0.3) !important",
                      },
                      "&:hover fieldset": {
                        borderColor: errors.startedYear ? "#d32f2f !important" : "#7c4dff !important",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: errors.startedYear ? "#d32f2f !important" : "#7c4dff !important",
                      },
                    },
                    input: {
                      color: isDark ? "#ffffff !important" : "#000000 !important",
                      backgroundColor: "transparent !important",
                    },
                  }}
                />
                {errors.startedYear && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block", textAlign: "left" }}
                  >
                    {errors.startedYear.message}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pt: 2, pb: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              minWidth: "220px",
              py: 1.2,
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #7c4dff 0%, #651fff 100%) !important",
              color: "#ffffff !important",
              "&:hover": {
                background: "linear-gradient(135deg, #651fff 0%, #4527a0 100%) !important",
              },
            }}
          >
            {loading ? "Saving..." : "Save and Continue"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
